import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  AppSvgIconComponent,
  SignalButton,
  SignalCheckbox,
  SignalDropdown,
  SignalRadio,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { CreateDataLibraryCollectionApiResponse } from '@core/api/workspace/models/createDataLibraryCollectionApiResponse';
import { CreateDataLibraryCollectionFieldRequestValueType } from '@core/api/workspace/models/createDataLibraryCollectionFieldRequestValueType';
import { dataLibraryTypeBadgeClass } from '../data-library-type-styles';

export type CollectionFieldValueType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date';

export interface ColumnDraft {
  id: number;
  label: string;
  valueType: CollectionFieldValueType;
  required: boolean;
}

const VALUE_TYPE_OPTIONS: { label: string; value: CollectionFieldValueType }[] =
  [
    { label: 'Text', value: 'text' },
    { label: 'Number', value: 'number' },
    { label: 'Boolean', value: 'boolean' },
    { label: 'Date', value: 'date' },
  ];

const VALUE_TYPE_ICONS: Record<CollectionFieldValueType, string> = {
  text: 'assets/icons/text-string.svg',
  number: 'assets/icons/numeric.svg',
  date: 'assets/icons/calendar-blank-outline.svg',
  boolean: 'assets/icons/toggle-switch-outline.svg',
};

const VALUE_TYPE_API: Record<
  CollectionFieldValueType,
  CreateDataLibraryCollectionFieldRequestValueType
> = {
  text: CreateDataLibraryCollectionFieldRequestValueType.text,
  number: CreateDataLibraryCollectionFieldRequestValueType.number,
  boolean: CreateDataLibraryCollectionFieldRequestValueType.boolean,
  date: CreateDataLibraryCollectionFieldRequestValueType.date,
};

let nextColumnId = 1;

export function createColumnDraft(
  overrides: Partial<ColumnDraft> = {},
): ColumnDraft {
  return {
    id: nextColumnId++,
    label: '',
    valueType: 'text',
    required: true,
    ...overrides,
  };
}

@Component({
  selector: 'app-create-collection-page',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    PageHeaderComponent,
    SignalButton,
    SignalCheckbox,
    SignalDropdown,
    SignalRadio,
    SignalTextField,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
  ],
  templateUrl: './create-collection-page.html',
  styleUrl: './create-collection-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCollectionPage extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly valueTypeOptions = VALUE_TYPE_OPTIONS;
  readonly createState = new State<CreateDataLibraryCollectionApiResponse>();

  readonly name = signal('');
  readonly columns = signal<ColumnDraft[]>([createColumnDraft()]);
  readonly uniqueColumnId = signal(this.columns()[0].id);

  cancel(): void {
    void this.router.navigate(['/data-library']);
  }

  onImportCsv(): void {
    this.toasterService.info('CSV import is coming soon.');
  }

  addColumn(): void {
    const draft = createColumnDraft();
    this.columns.update((cols) => [...cols, draft]);
    this.scrollToColumn(draft.id);
  }

  removeColumn(id: number): void {
    const cols = this.columns();
    if (cols.length <= 1) {
      return;
    }

    const next = cols.filter((col) => col.id !== id);
    this.columns.set(next);

    if (this.uniqueColumnId() === id) {
      this.uniqueColumnId.set(next[0].id);
    }
  }

  private scrollToColumn(id: number): void {
    setTimeout(() => {
      const el = document.querySelector(
        `[data-column-id="${id}"]`,
      ) as HTMLElement | null;
      if (!el) {
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const input = el.querySelector('input') as HTMLInputElement | null;
      input?.focus({ preventScroll: true });
    });
  }

  setLabel(id: number, label: string): void {
    this.patchColumn(id, { label });
  }

  setValueType(id: number, value: unknown): void {
    this.patchColumn(id, { valueType: value as CollectionFieldValueType });
  }

  iconSrcForType(valueType: CollectionFieldValueType): string {
    return VALUE_TYPE_ICONS[valueType];
  }

  typeChipClass(valueType: CollectionFieldValueType): string {
    return dataLibraryTypeBadgeClass(valueType);
  }

  setRequired(id: number, required: boolean): void {
    this.patchColumn(id, { required });
  }

  setUnique(id: unknown): void {
    if (typeof id !== 'number') {
      return;
    }
    this.uniqueColumnId.set(id);
  }

  onColumnDrop(event: CdkDragDrop<ColumnDraft[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const cols = [...this.columns()];
    moveItemInArray(cols, event.previousIndex, event.currentIndex);
    this.columns.set(cols);
  }

  createCollection(): void {
    const name = this.name().trim();
    if (!name) {
      this.toasterService.error('Enter a name.');
      return;
    }

    const columns = this.columns();
    if (columns.length === 0) {
      this.toasterService.error('Add at least one column.');
      return;
    }

    for (const column of columns) {
      if (!column.label.trim()) {
        this.toasterService.error('Enter a label for every column.');
        return;
      }
    }

    const uniqueIndex = columns.findIndex(
      (column) => column.id === this.uniqueColumnId(),
    );
    if (uniqueIndex < 0) {
      this.toasterService.error('Select a unique column.');
      return;
    }

    const uniqueColumn = columns[uniqueIndex];
    if (
      uniqueColumn.valueType !== 'text' &&
      uniqueColumn.valueType !== 'number'
    ) {
      this.toasterService.error(
        'The unique column must be Text or Number.',
      );
      return;
    }

    if (!columns.some((column) => column.valueType === 'text')) {
      this.toasterService.error('Add at least one Text column.');
      return;
    }

    this.executeRequest({
      state: this.createState,
      request: this.userAPIService.createDataLibraryCollection({
        name,
        fields: columns.map((column) => ({
          name: column.label.trim(),
          valueType: VALUE_TYPE_API[column.valueType],
          required: column.required,
        })),
        uniqueFieldIndex: uniqueIndex,
      }),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        this.toasterService.success('Collection created.');
        void this.router.navigate([
          '/data-library/collections',
          response.data.id,
        ]);
      },
    });
  }

  private patchColumn(id: number, patch: Partial<ColumnDraft>): void {
    this.columns.update((cols) =>
      cols.map((col) => (col.id === id ? { ...col, ...patch } : col)),
    );
  }
}
